/**
 * Jun-2026 (User-Wunsch) — KI-Daten-Postfach (daten@twowheels.info):
 * Eingehende E-Mails + Anhänge werden per KI ausgelesen und automatisch
 * in die KI-Wissensdatenbank übernommen. Cron alle 10 Min + manueller Abruf.
 */
import { useEffect, useState } from "react";
import { Mail, RefreshCw, BookOpenCheck } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";

export const KnowledgeMailCard = () => {
    const [data, setData] = useState(null);
    const [busy, setBusy] = useState(false);
    const [accountId, setAccountId] = useState("");
    const [enabled, setEnabled] = useState(false);

    const load = async () => {
        try {
            const { data: d } = await api.get("/admin/knowledge-mail");
            setData(d);
            setAccountId(d.settings?.account_id || "");
            setEnabled(!!d.settings?.enabled);
        } catch { /* ignore */ }
    };
    useEffect(() => { load(); }, []);

    const save = async (nextEnabled = enabled, nextAccount = accountId) => {
        setBusy(true);
        try {
            await api.post("/admin/knowledge-mail", { account_id: nextAccount || null, enabled: nextEnabled });
            toast.success("KI-Daten-Postfach gespeichert");
            await load();
        } catch (e) { toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen"); }
        setBusy(false);
    };

    const runNow = async () => {
        setBusy(true);
        try {
            const { data: r } = await api.post("/admin/knowledge-mail/run");
            if (r.skipped) toast.info("Postfach nicht konfiguriert oder deaktiviert.");
            else if (r.ok) toast.success(`${r.mails ?? 0} E-Mail(s) geprüft · ${r.documents_created ?? 0} Wissens-Einträge erstellt`);
            else toast.error(r.error || "Abruf fehlgeschlagen");
            await load();
        } catch (e) { toast.error(e.response?.data?.detail || "Abruf fehlgeschlagen"); }
        setBusy(false);
    };

    const s = data?.settings || {};
    return (
        <section className="mt-10" data-testid="knowledge-mail-section">
            <h2 className="font-display text-lg font-black mb-3 flex items-center gap-2">
                <BookOpenCheck className="w-5 h-5 text-[#FFCC00]" /> KI-Daten-Postfach → Wissensdatenbank
            </h2>
            <div className="bg-white border-2 border-[#111827] rounded-sm p-4">
                <p className="text-xs text-gray-600 max-w-3xl">
                    E-Mails an das gewählte Postfach (z.&nbsp;B. <b>daten@twowheels.info</b>) werden automatisch alle 10 Minuten
                    abgerufen, per KI ausgelesen (inkl. Anhänge: PDF, TXT, MD, CSV, XLSX, DOCX) und als Einträge in die
                    KI-Wissensdatenbank übernommen. Das Postfach muss zuerst im E-Mail-Center (IMAP) angelegt sein.
                </p>
                <div className="mt-3 flex flex-wrap items-center gap-3">
                    <select value={accountId} onChange={(e) => setAccountId(e.target.value)}
                        className="border border-gray-300 rounded-sm px-3 py-2 text-sm min-w-[260px]"
                        data-testid="knowledge-mail-account-select">
                        <option value="">— Postfach wählen —</option>
                        {(data?.accounts || []).map((a) => (
                            <option key={a.id} value={a.id}>{a.email_address || a.label} ({a.type})</option>
                        ))}
                    </select>
                    <label className="inline-flex items-center gap-2 text-sm font-bold cursor-pointer" data-testid="knowledge-mail-enabled-label">
                        <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)}
                            data-testid="knowledge-mail-enabled-toggle" />
                        Automatischer Abruf aktiv
                    </label>
                    <button onClick={() => save()} disabled={busy}
                        className="px-4 py-2 rounded-sm bg-[#111827] text-[#FFCC00] text-xs font-bold uppercase tracking-wider hover:bg-black disabled:opacity-50"
                        data-testid="knowledge-mail-save-btn">
                        Speichern
                    </button>
                    <button onClick={runNow} disabled={busy || !s.account_id}
                        className="px-4 py-2 rounded-sm border border-gray-300 text-xs font-bold uppercase tracking-wider hover:border-[#111827] disabled:opacity-50 inline-flex items-center gap-1.5"
                        data-testid="knowledge-mail-run-btn">
                        <RefreshCw className={`w-3.5 h-3.5 ${busy ? "animate-spin" : ""}`} /> Jetzt abrufen
                    </button>
                </div>
                <div className="mt-2 text-[11px] text-gray-500" data-testid="knowledge-mail-status">
                    {s.last_run_at ? `Letzter Lauf: ${new Date(s.last_run_at).toLocaleString("de-DE")}` : "Noch kein Lauf."}
                    {" · "}{s.processed_total || 0} Einträge insgesamt
                    {s.last_error && <span className="text-red-600 font-bold"> · Fehler: {s.last_error}</span>}
                </div>
                {(data?.recent || []).length > 0 && (
                    <div className="mt-3 border-t border-gray-100 pt-2" data-testid="knowledge-mail-recent">
                        <div className="text-[10px] font-bold uppercase tracking-wider text-gray-500 mb-1">Zuletzt übernommen</div>
                        {(data.recent || []).map((r) => (
                            <div key={r.id} className="text-xs text-gray-700 flex items-center gap-2 py-0.5">
                                <Mail className="w-3 h-3 text-gray-400 shrink-0" />
                                <span className="truncate">{r.filename}</span>
                                <span className="text-gray-400 shrink-0">{new Date(r.created_at).toLocaleString("de-DE")}</span>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </section>
    );
};

export default KnowledgeMailCard;
